import { useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Award,
  BookOpenCheck,
  Brain,
  CheckCircle2,
  ClipboardCheck,
  FileCheck2,
  HelpCircle,
  ShieldCheck,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

import SEO from "@/components/site/SEO";
import CTASection from "@/components/site/CTASection";

const COURSE_PAGES = [
  {
    id: "title",
    type: "title",
    eyebrow: "TRAC-Learn example course",
    title: "Operational Thinking",
    subtitle:
      "A short eLearning example for railway operational judgement, risk awareness and decision making under pressure.",
  },
  {
    id: "klps",
    type: "content",
    eyebrow: "Key learning points",
    title: "By the end of this short example, the learner should understand how to:",
    icon: BookOpenCheck,
    content: [
      "Recognise when an operational situation has changed.",
      "Pause and assess risk before acting.",
      "Use structured judgement rather than assumptions.",
      "Communicate clearly when information is uncertain.",
      "Make decisions that can be explained and evidenced afterwards.",
    ],
  },
  {
    id: "intro",
    type: "content",
    eyebrow: "Introduction",
    title: "Operational thinking is the discipline of thinking before acting.",
    icon: Brain,
    content: [
      "In safety-critical railway work, not every risk is obvious. Some risks appear because something has changed, information is incomplete or pressure is being applied to keep the service moving.",
      "Operational thinking helps the learner slow the decision down just enough to make a safe, proportionate and defensible choice.",
      "This mini course demonstrates how a digital module could combine learning content, scenarios, knowledge checks, feedback and completion evidence.",
    ],
  },
  {
    id: "section-1",
    type: "content",
    eyebrow: "Section 1",
    title: "Recognising changed risk",
    icon: AlertTriangle,
    content: [
      "A routine task can stop being routine when the context changes. A delay, unusual instruction, missing information or unfamiliar environment can all increase operational risk.",
      "The first skill is recognising the moment where a pause is needed. This does not mean stopping work unnecessarily. It means identifying when the situation now needs conscious judgement.",
      "A useful prompt is: What has changed, what do I know, what do I not know, and what must be confirmed before I proceed?",
    ],
  },
  {
    id: "section-2",
    type: "content",
    eyebrow: "Section 2",
    title: "Using structured judgement",
    icon: ShieldCheck,
    content: [
      "Operational thinking is not guesswork. It is a structured approach to judgement under uncertainty.",
      "The learner should consider rules, local instructions, competence, available information, communication channels and the consequences of acting too quickly.",
      "Good operational judgement balances safety, compliance and service delivery, but it never trades away essential safety assurance simply to save time.",
    ],
  },
  {
    id: "scenario",
    type: "scenario",
    eyebrow: "Scenario activity",
    title: "A routine movement starts to feel less routine.",
    scenario:
      "You are involved in a routine operational movement that is already running late. You are asked to proceed promptly to recover time. While preparing, you notice that one piece of information does not match what you expected. There is no emergency, but there is enough uncertainty to make you pause.",
  },
  {
    id: "assessment",
    type: "assessment",
    eyebrow: "Assessment",
    title: "Short knowledge check",
  },
  {
    id: "feedback",
    type: "feedback",
    eyebrow: "Feedback",
    title: "Your course feedback",
  },
  {
    id: "summary",
    type: "summary",
    eyebrow: "Summary",
    title: "Operational thinking supports safer, more defensible decisions.",
  },
  {
    id: "certificate",
    type: "certificate",
    eyebrow: "Example certificate",
    title: "Certificate of Completion",
  },
];

const SCENARIO_OPTIONS = [
  {
    id: "a",
    label: "Continue as planned because the delay is minor.",
    correct: false,
    feedback:
      "This response gives too much weight to service recovery and not enough weight to the change in risk. A minor delay does not remove the need to confirm uncertain information.",
  },
  {
    id: "b",
    label:
      "Pause, reassess the risk, confirm the information, communicate clearly and then proceed only when satisfied it is safe and authorised.",
    correct: true,
    feedback:
      "This is the strongest response. It demonstrates operational thinking by recognising uncertainty, managing pressure, confirming information and making a controlled decision.",
  },
  {
    id: "c",
    label:
      "Ask a colleague what they would do and follow their advice to avoid delaying the job further.",
    correct: false,
    feedback:
      "Seeking input can be useful, but this response risks outsourcing responsibility. Advice may support the decision, but the learner still needs to apply rules, competence and structured judgement.",
  },
];

const ASSESSMENT_QUESTIONS = [
  {
    id: "q1",
    question:
      "Which behaviour best demonstrates operational thinking in a safety-critical environment?",
    answers: [
      {
        id: "q1a",
        text: "Act quickly to reduce delay wherever possible.",
        correct: false,
      },
      {
        id: "q1b",
        text: "Pause, assess the risk, confirm information and act within rules and authority.",
        correct: true,
      },
      {
        id: "q1c",
        text: "Rely mainly on experience because experienced people usually know best.",
        correct: false,
      },
    ],
  },
  {
    id: "q2",
    question: "What should a learner do when information does not match what they expected?",
    answers: [
      {
        id: "q2a",
        text: "Ignore it if the task is normally routine.",
        correct: false,
      },
      {
        id: "q2b",
        text: "Treat it as a prompt to reassess and confirm before proceeding.",
        correct: true,
      },
      {
        id: "q2c",
        text: "Proceed, but make a note afterwards.",
        correct: false,
      },
    ],
  },
  {
    id: "q3",
    question:
      "Why is recording or evidencing learning useful in an operational training system?",
    answers: [
      {
        id: "q3a",
        text: "It supports completion records, manager visibility, competence evidence and assurance.",
        correct: true,
      },
      {
        id: "q3b",
        text: "It removes the need for practical training or manager review.",
        correct: false,
      },
      {
        id: "q3c",
        text: "It is only useful for issuing certificates.",
        correct: false,
      },
    ],
  },
];

export default function Elearning() {
  const [currentPage, setCurrentPage] = useState(0);
  const [scenarioAnswer, setScenarioAnswer] = useState(null);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [showAssessmentFeedback, setShowAssessmentFeedback] = useState(false);
  const [learnerName, setLearnerName] = useState("Example Learner");

  const page = COURSE_PAGES[currentPage];
  const progress = Math.round(((currentPage + 1) / COURSE_PAGES.length) * 100);

  const scenarioSelection = SCENARIO_OPTIONS.find(
    (option) => option.id === scenarioAnswer
  );

  const score = useMemo(() => {
    return ASSESSMENT_QUESTIONS.reduce((total, question) => {
      const selected = question.answers.find(
        (answer) => answer.id === assessmentAnswers[question.id]
      );

      return selected?.correct ? total + 1 : total;
    }, 0);
  }, [assessmentAnswers]);

  const assessmentComplete =
    Object.keys(assessmentAnswers).length === ASSESSMENT_QUESTIONS.length;

  const percentageScore = Math.round((score / ASSESSMENT_QUESTIONS.length) * 100);
  const passed = assessmentComplete && percentageScore >= 80;

  const goNext = () => {
    setCurrentPage((value) => Math.min(value + 1, COURSE_PAGES.length - 1));
  };

  const goBack = () => {
    setCurrentPage((value) => Math.max(value - 1, 0));
  };

  const goToPage = (index) => {
    setCurrentPage(index);
  };

  return (
    <>
      <SEO
        title="Operational Thinking eLearning Example | TRAC-Learn"
        description="Explore a small example of a structured operational thinking eLearning course with learning pages, assessment, feedback, summary and certificate."
        path="/elearning"
      />

      <section className="relative overflow-hidden bg-navy py-16 md:py-20 text-white">
        <div className="container-rail">
          <Link
            to="/for-organisations"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to TRAC
          </Link>

          <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_360px] lg:items-center">
            <div>
              <p className="label-eyebrow text-white/70">
                TRAC-Learn eLearning example
              </p>

              <h1 className="mt-4 font-display font-bold text-4xl md:text-6xl leading-tight text-white">
                Operational Thinking
              </h1>

              <p className="mt-6 max-w-3xl text-lg md:text-xl leading-relaxed text-white/80">
                A miniature example of a structured railway eLearning course
                with navigable pages, key learning points, scenario learning,
                assessment, feedback, summary and a sample certificate.
              </p>
            </div>

            <div className="rounded-md border border-white/10 bg-white/5 p-6 shadow-sm">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-md bg-white/10">
                <Brain className="h-7 w-7 text-white" />
              </div>

              <h2 className="mt-5 font-display font-bold text-2xl leading-tight">
                Mini LMS-style module
              </h2>

              <p className="mt-4 text-white/80 leading-relaxed">
                This page demonstrates the structure of a digital course rather
                than a full production module.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-slate-50">
        <div className="container-rail">
          <div className="grid gap-8 lg:grid-cols-[280px_1fr] lg:items-start">
            <aside className="rounded-md border border-slate-200 bg-white p-5 shadow-sm lg:sticky lg:top-24">
              <p className="text-xs font-bold uppercase tracking-wide text-safety">
                Course navigation
              </p>

              <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-safety transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <p className="mt-2 text-xs text-slate-500">
                Progress: {progress}%
              </p>

              <nav className="mt-5 grid gap-2">
                {COURSE_PAGES.map((item, index) => {
                  const isActive = index === currentPage;
                  const isCompleted = index < currentPage;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => goToPage(index)}
                      className={`flex items-center gap-3 rounded-md border px-3 py-3 text-left text-sm transition-all ${
                        isActive
                          ? "border-safety bg-safety/10 text-navy shadow-sm"
                          : "border-slate-200 bg-white text-slate-600 hover:border-safety/60"
                      }`}
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                          isCompleted
                            ? "bg-safety text-white"
                            : isActive
                            ? "bg-navy text-white"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                      </span>

                      <span className="font-semibold">{item.eyebrow}</span>
                    </button>
                  );
                })}
              </nav>
            </aside>

            <div className="rounded-md border border-slate-200 bg-white p-6 md:p-10 shadow-sm min-h-[620px]">
              {page.type === "title" && (
                <div className="flex min-h-[520px] flex-col justify-center">
                  <p className="label-eyebrow text-safety">{page.eyebrow}</p>

                  <h2 className="mt-4 font-display font-bold text-4xl md:text-5xl text-navy leading-tight">
                    {page.title}
                  </h2>

                  <p className="mt-6 max-w-3xl text-lg leading-relaxed text-slate-600">
                    {page.subtitle}
                  </p>

                  <div className="mt-8 grid gap-4 md:grid-cols-3">
                    {[
                      "Estimated time: 10 minutes",
                      "Format: self-paced eLearning",
                      "Assessment: 3 questions",
                    ].map((item) => (
                      <div
                        key={item}
                        className="rounded-md border border-slate-200 bg-slate-50 p-4"
                      >
                        <p className="text-sm font-bold text-navy">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {page.type === "content" && (
                <div>
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-navy text-white">
                    <page.icon className="h-6 w-6" />
                  </div>

                  <p className="mt-6 label-eyebrow text-safety">
                    {page.eyebrow}
                  </p>

                  <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                    {page.title}
                  </h2>

                  <div className="mt-8 grid gap-4">
                    {page.content.map((paragraph, index) => (
                      <div
                        key={paragraph}
                        className="rounded-md border border-slate-200 bg-slate-50 p-5"
                      >
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-safety" />
                          <p className="leading-relaxed text-slate-700">
                            {paragraph}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {page.type === "scenario" && (
                <div>
                  <p className="label-eyebrow text-safety">{page.eyebrow}</p>

                  <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                    {page.title}
                  </h2>

                  <div className="mt-8 rounded-md border border-amber-200 bg-amber-50 p-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="mt-1 h-6 w-6 shrink-0 text-amber-700" />
                      <p className="leading-relaxed text-amber-950">
                        {page.scenario}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 rounded-md border border-slate-200 bg-white p-6">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="mt-1 h-6 w-6 shrink-0 text-safety" />
                      <div>
                        <h3 className="font-display font-bold text-2xl text-navy">
                          What should you do?
                        </h3>

                        <p className="mt-2 text-sm leading-relaxed text-slate-600">
                          Select the response that best demonstrates operational
                          thinking.
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 grid gap-3">
                      {SCENARIO_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setScenarioAnswer(option.id)}
                          className={`rounded-md border p-4 text-left transition-all ${
                            scenarioAnswer === option.id
                              ? "border-safety bg-safety/10 shadow-sm"
                              : "border-slate-200 bg-slate-50 hover:border-safety/60 hover:bg-white"
                          }`}
                        >
                          <p className="text-sm font-semibold text-navy">
                            {option.label}
                          </p>
                        </button>
                      ))}
                    </div>

                    {scenarioSelection && (
                      <div
                        className={`mt-6 rounded-md border p-5 ${
                          scenarioSelection.correct
                            ? "border-emerald-200 bg-emerald-50"
                            : "border-amber-200 bg-amber-50"
                        }`}
                      >
                        <p
                          className={`text-xs font-bold uppercase tracking-wide ${
                            scenarioSelection.correct
                              ? "text-emerald-700"
                              : "text-amber-700"
                          }`}
                        >
                          {scenarioSelection.correct
                            ? "Strong response"
                            : "Development feedback"}
                        </p>

                        <p
                          className={`mt-2 text-sm leading-relaxed ${
                            scenarioSelection.correct
                              ? "text-emerald-900"
                              : "text-amber-900"
                          }`}
                        >
                          {scenarioSelection.feedback}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {page.type === "assessment" && (
                <div>
                  <p className="label-eyebrow text-safety">{page.eyebrow}</p>

                  <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                    {page.title}
                  </h2>

                  <p className="mt-4 text-slate-600 leading-relaxed">
                    Answer the questions below. This example assessment uses a
                    simple 80% pass mark to demonstrate how completion evidence
                    could be generated.
                  </p>

                  <div className="mt-8 grid gap-6">
                    {ASSESSMENT_QUESTIONS.map((question, qIndex) => (
                      <div
                        key={question.id}
                        className="rounded-md border border-slate-200 bg-slate-50 p-6"
                      >
                        <p className="text-xs font-bold uppercase tracking-wide text-safety">
                          Question {qIndex + 1}
                        </p>

                        <h3 className="mt-2 font-display font-bold text-xl text-navy">
                          {question.question}
                        </h3>

                        <div className="mt-5 grid gap-3">
                          {question.answers.map((answer) => (
                            <button
                              key={answer.id}
                              type="button"
                              onClick={() =>
                                setAssessmentAnswers((current) => ({
                                  ...current,
                                  [question.id]: answer.id,
                                }))
                              }
                              className={`rounded-md border p-4 text-left transition-all ${
                                assessmentAnswers[question.id] === answer.id
                                  ? "border-safety bg-white shadow-sm"
                                  : "border-slate-200 bg-white hover:border-safety/60"
                              }`}
                            >
                              <p className="text-sm font-semibold text-navy">
                                {answer.text}
                              </p>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowAssessmentFeedback(true)}
                    disabled={!assessmentComplete}
                    className="mt-8 inline-flex items-center justify-center gap-2 rounded-md bg-safety px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-safety/90 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    Submit assessment
                    <FileCheck2 className="h-4 w-4" />
                  </button>

                  {!assessmentComplete && (
                    <p className="mt-3 text-sm text-slate-500">
                      Answer all questions to submit the assessment.
                    </p>
                  )}

                  {showAssessmentFeedback && (
                    <div
                      className={`mt-6 rounded-md border p-5 ${
                        passed
                          ? "border-emerald-200 bg-emerald-50"
                          : "border-amber-200 bg-amber-50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {passed ? (
                          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                        ) : (
                          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                        )}

                        <div>
                          <p
                            className={`font-bold ${
                              passed ? "text-emerald-900" : "text-amber-900"
                            }`}
                          >
                            {passed
                              ? `Passed: ${percentageScore}%`
                              : `Not yet passed: ${percentageScore}%`}
                          </p>

                          <p
                            className={`mt-2 text-sm leading-relaxed ${
                              passed ? "text-emerald-900" : "text-amber-900"
                            }`}
                          >
                            {passed
                              ? "You have demonstrated the core principles of operational thinking in this short example."
                              : "Review the learning pages and try again. Operational thinking requires structured judgement, risk recognition and clear communication."}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {page.type === "feedback" && (
                <div>
                  <p className="label-eyebrow text-safety">{page.eyebrow}</p>

                  <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                    {page.title}
                  </h2>

                  <div className="mt-8 grid gap-5">
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-6">
                      <p className="text-sm font-bold text-navy">
                        Scenario activity
                      </p>

                      <p className="mt-2 text-sm leading-relaxed text-slate-600">
                        {scenarioSelection
                          ? scenarioSelection.feedback
                          : "No scenario answer selected yet. Return to the scenario page to complete the activity."}
                      </p>
                    </div>

                    <div className="rounded-md border border-slate-200 bg-slate-50 p-6">
                      <p className="text-sm font-bold text-navy">
                        Assessment result
                      </p>

                      <p className="mt-2 text-sm leading-relaxed text-slate-600">
                        {assessmentComplete
                          ? `Current score: ${score} out of ${ASSESSMENT_QUESTIONS.length} (${percentageScore}%). ${
                              passed
                                ? "This meets the example pass mark."
                                : "This does not yet meet the example pass mark."
                            }`
                          : "Assessment not yet completed."}
                      </p>
                    </div>

                    <div className="rounded-md border border-slate-200 bg-slate-50 p-6">
                      <p className="text-sm font-bold text-navy">
                        Development message
                      </p>

                      <p className="mt-2 text-sm leading-relaxed text-slate-600">
                        Strong operational thinking is not just about knowing
                        the rules. It is about recognising changed risk, using
                        structured judgement and making decisions that remain
                        safe, compliant and explainable.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {page.type === "summary" && (
                <div>
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-navy text-white">
                    <ClipboardCheck className="h-6 w-6" />
                  </div>

                  <p className="mt-6 label-eyebrow text-safety">
                    {page.eyebrow}
                  </p>

                  <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                    {page.title}
                  </h2>

                  <div className="mt-8 grid gap-4">
                    {[
                      "Operational thinking starts with recognising when risk has changed.",
                      "A pause can be a positive safety behaviour when information is uncertain.",
                      "Structured judgement helps avoid rushing, assuming or outsourcing responsibility.",
                      "Clear communication supports safe and coordinated operational decisions.",
                      "Digital learning can create completion evidence, assessment records and manager visibility.",
                    ].map((item) => (
                      <div
                        key={item}
                        className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-5"
                      >
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-safety" />
                        <p className="text-slate-700">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {page.type === "certificate" && (
                <div>
                  <p className="label-eyebrow text-safety">{page.eyebrow}</p>

                  <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                    {page.title}
                  </h2>

                  <p className="mt-4 text-slate-600 leading-relaxed">
                    A full TRAC-Learn module could generate completion evidence
                    and certificates for CPD, internal records or manager review.
                    This is a simple visual example.
                  </p>

                  <div className="mt-8 rounded-lg border-4 border-double border-navy bg-white p-8 text-center shadow-sm">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-safety text-white">
                      <Award className="h-8 w-8" />
                    </div>

                    <p className="mt-6 text-xs font-bold uppercase tracking-[0.3em] text-safety">
                      Certificate of Completion
                    </p>

                    <h3 className="mt-4 font-display text-4xl font-bold text-navy">
                      Operational Thinking
                    </h3>

                    <p className="mt-6 text-slate-600">
                      This certifies that
                    </p>

                    <div className="mx-auto mt-4 max-w-md">
                      <input
                        type="text"
                        value={learnerName}
                        onChange={(event) => setLearnerName(event.target.value)}
                        className="w-full border-b-2 border-slate-300 bg-transparent px-3 py-2 text-center font-display text-2xl font-bold text-navy outline-none focus:border-safety"
                        aria-label="Learner name"
                      />
                    </div>

                    <p className="mt-6 text-slate-600">
                      has completed the example TRAC-Learn module and achieved
                      an example assessment score of
                    </p>

                    <p className="mt-3 font-display text-3xl font-bold text-safety">
                      {assessmentComplete ? `${percentageScore}%` : "Not yet assessed"}
                    </p>

                    <div className="mt-8 grid gap-4 border-t border-slate-200 pt-6 text-sm text-slate-600 md:grid-cols-3">
                      <div>
                        <p className="font-bold text-navy">Course</p>
                        <p>Operational Thinking</p>
                      </div>

                      <div>
                        <p className="font-bold text-navy">Provider</p>
                        <p>TRAC-Learn</p>
                      </div>

                      <div>
                        <p className="font-bold text-navy">Status</p>
                        <p>{passed ? "Completed" : "Example only"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-10 flex flex-col gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={currentPage === 0}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-navy transition-all hover:border-safety disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Previous
                </button>

                <p className="text-center text-sm text-slate-500">
                  Page {currentPage + 1} of {COURSE_PAGES.length}
                </p>

                <button
                  type="button"
                  onClick={goNext}
                  disabled={currentPage === COURSE_PAGES.length - 1}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-safety px-5 py-3 text-sm font-bold text-white transition-all hover:bg-safety/90 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  Next
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="TRAC-Learn"
        title="Discuss eLearning for operational thinking."
        intro="TRAC-Learn can support structured eLearning, scenario-based training, knowledge checks, CPD evidence, completion records and operational risk development for railway organisations."
        primary={{
          label: "Discuss TRAC-Learn",
          to: "/contact",
          testId: "elearning-cta-contact",
        }}
        secondary={{
          label: "Back to TRAC",
          to: "/for-organisations",
          testId: "elearning-cta-trac",
        }}
      />
    </>
  );
}